import { useFeatureFlagsApi } from '@/api/organizations';
import { getStorage, setStorage } from '@/hooks/useLocalStorage';
import { type UiFeatureFlags } from '@/types/FeatureFlags';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

type StoredUiFeatureFlags = UiFeatureFlags & {
  lastFetched: number | null;
};

export const uiFeatureFlagsQueryKey = ['ui-feature-flags'];

const FEATURE_FLAGS_STORAGE_KEY = 'ui-feature-flags';

function readStoredFeatureFlags(): StoredUiFeatureFlags | undefined {
  const restored = getStorage(FEATURE_FLAGS_STORAGE_KEY) as Partial<StoredUiFeatureFlags> | null;

  if (!restored) {
    return undefined;
  }

  return {
    viewVendors: Boolean(restored.viewVendors),
    viewDeliveryFailureWarning: Boolean(restored.viewDeliveryFailureWarning),
    viewAllocation: Boolean(restored.viewAllocation),
    viewAttachmentTypeSearchFilters: Boolean(restored.viewAttachmentTypeSearchFilters),
    viewClosingSearchFilters: Boolean(restored.viewClosingSearchFilters),
    viewOrderEventSearch: Boolean(restored.viewOrderEventSearch),
    viewClearToCloseDocumentationReport: Boolean(restored.viewClearToCloseDocumentationReport),
    viewEmailSubscription: Boolean(restored.viewEmailSubscription),
    viewEventTypeAdvancedFilters: Boolean(restored.viewEventTypeAdvancedFilters),
    showAllAllocationOperatorOptions: Boolean(restored.showAllAllocationOperatorOptions),
    createAttributeDictionary: Boolean(restored.createAttributeDictionary),
    tenantConfigFeatureControl: Boolean(restored.tenantConfigFeatureControl),
    lastFetched:
      typeof restored.lastFetched === 'number' && Number.isFinite(restored.lastFetched)
        ? restored.lastFetched
        : null,
  };
}

export const useGetUiFeatureFlags = () => {
  const featureFlagsApi = useFeatureFlagsApi();
  const initialStoredFlags = useMemo(() => readStoredFeatureFlags(), []);

  const query = useQuery({
    queryKey: uiFeatureFlagsQueryKey,
    queryFn: async (): Promise<StoredUiFeatureFlags> => {
      // Read the untransformed payload: the generated response mapper only copies fields
      // that exist in the OpenAPI spec, so locally-declared flags the backend already
      // returns (e.g. viewOrderEventSearch, viewEmailSubscription) would be dropped.
      const { raw } = await featureFlagsApi.getFeatureFlagsRaw();
      const response = (await raw.json()) as Partial<UiFeatureFlags> | null;

      return {
        viewVendors: response?.viewVendors ?? false,
        viewDeliveryFailureWarning: response?.viewDeliveryFailureWarning ?? false,
        viewAllocation: response?.viewAllocation ?? false,
        viewAttachmentTypeSearchFilters: response?.viewAttachmentTypeSearchFilters ?? false,
        viewClosingSearchFilters: response?.viewClosingSearchFilters ?? false,
        viewOrderEventSearch: response?.viewOrderEventSearch ?? false,
        viewClearToCloseDocumentationReport: response?.viewClearToCloseDocumentationReport ?? false,
        viewEmailSubscription: response?.viewEmailSubscription ?? false,
        viewEventTypeAdvancedFilters: response?.viewEventTypeAdvancedFilters ?? false,
        showAllAllocationOperatorOptions: response?.showAllAllocationOperatorOptions ?? false,
        createAttributeDictionary: response?.createAttributeDictionary ?? false,
        tenantConfigFeatureControl: response?.tenantConfigFeatureControl ?? false,
        lastFetched: Date.now(),
      };
    },
    initialData: initialStoredFlags,
    initialDataUpdatedAt: initialStoredFlags?.lastFetched ?? undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  //  Persist last known good flags to localStorage.
  useEffect(() => {
    if (query.data) {
      setStorage(FEATURE_FLAGS_STORAGE_KEY, query.data);
    }
  }, [query.data]);

  return query;
};
